import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
	useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import * as Yup from 'yup';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import {
	useCloseAllCaseMutation,
	useCloseNotSuitableMutation,
	useGetFamilyMemberGroupListMutation,
} from '@/api/member-management/admissionRequest';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useGetDicListQuery } from '@/api/common';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import AppTextField from '@/common/components/form-inputs/AppTextField';

interface Props {
	open: boolean;
	onClose: () => void;
}

const CloseNotSuitableDialog = (props: Props) => {
	const { open, onClose } = props;
	const { t } = useTranslation();
	const [familyMemberList, setFamilyMemberList] = useState<any>([]);
	const [openAssignProgramWorkers, setOpenAssignProgramWorkers] =
		useState(false);
	const [memberForm, setMemberForm] = useState({});
	const [getFamilyMemberGroupList] = useGetFamilyMemberGroupListMutation();
	const [closeNotSuitable] = useCloseNotSuitableMutation();
	const { admissionRequestId } = useParams();
	const location = useLocation();
	const memberType = i18n.t(
		location.pathname.includes('admission-request-list')
			? 'member.admissionRequestLabel'
			: 'member.memberDetailsLabel'
	);

	const userInfo = useSelector(selectMyInfo);

	const { data: closedReasonDicList } = useGetDicListQuery(
		'not_suitable_reason',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		notSuitableReason: Yup.string().required(t('errorMessage.required')),
		notSuitableReasonSpecify: Yup.string().when('notSuitableReason', {
			is: (a: string) => a && notSuitableReasonIsOther(),
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
	});
	const formSchema2 = Yup.object().shape({});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const watchNotSuitableReason = methods.watch('notSuitableReason');

	const notSuitableReasonIsOther = () => {
		const obj = closedReasonDicList?.data.find(
			(item: any) => item.id == watchNotSuitableReason
		);
		if (obj && obj.code == '12') return true;
		return false;
	};
	const methods2 = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema2),
	});

	const handleClose = () => {
		methods.reset({});
		onClose();
	};

	const handleCloseOtherDialog = () => {
		methods2.reset({});
		onClose();
	};

	const submitClose = async (data: any) => {
		const response = await closeNotSuitable({
			...data,
			id: admissionRequestId,
		});
		if ('data' in response) {
			enqueueSnackbar(
				t('snackbar.updateCloseNotSuitableSuccess', { type: memberType }),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			handleClose();
			handleCloseOtherDialog();
		}
	};

	const onNext: SubmitHandler<any> = async (data) => {
		setMemberForm(data);

		const response = await getFamilyMemberGroupList(admissionRequestId);
		if ('data' in response) {
			const list = response?.data?.data || [];
			setFamilyMemberList(list);
			// No associated members to remind about, close directly
			if (list.length === 0) {
				await submitClose(data);
				return;
			}
		}
		setOpenAssignProgramWorkers(true);
	};

	const onSubmit: SubmitHandler<any> = async () => {
		await submitClose(memberForm);
	};

	const columns: GridColDef[] = [
		{
			field: 'targetMemberNameEn',
			headerName: t('member.name'),
			flex: 10,
			renderCell: (params: any) => (
				<Box>
					<Typography variant='body2'>
						{params.row.targetMemberNameEn}
					</Typography>
					<Typography variant='body2'>
						{params.row.targetMemberNameZh}
					</Typography>
				</Box>
			),
		},
		{
			field: 'relationshipDesc',
			headerName: t('preAdmission.relationship'),
			flex: 10,
		},
		{
			field: 'targetFamilyMembership',
			headerName: t('member.membershipRole'),
			flex: 10,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<a
					href={
						params.row.targetMemberIdDesc
							? `/member-management/member-list/detail/${params.row.targetMemberIdDesc}`
							: `/family-management/family-management-list/detail/${params.row.targetFamilyMemberIdDesc}`
					}
					target='_blank'
					rel='noopener noreferrer'
				>
					<IconButton color='secondary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</a>
			),
		},
	];

	return (
		<>
			<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
				<DialogTitle variant='h5'>
					{t('member.closeNotSuitableConfirmation')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('member.closeNotSuitableConfirmationText')}
					</Typography>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onNext)}>
							<Grid
								container
								spacing={2}
								marginTop={0}
								marginBottom={2}
								alignItems='center'
							>
								<Grid size={12}>
									<AppDropDownMenu
										control={methods.control}
										name='notSuitableReason'
										label={t('member.notSuitableReason') + '*'}
										options={(closedReasonDicList?.data || []).map(
											(item: { labelEn: any }) => {
												return {
													...item,
													name: item.labelEn,
												};
											}
										)}
										handleChange={() => {
											methods.setValue('notSuitableReasonSpecify', '');
											methods.clearErrors('notSuitableReasonSpecify');
										}}
									/>
								</Grid>
								<Grid size={12}>
									<AppTextField
										disabled={!notSuitableReasonIsOther()}
										control={methods.control}
										name='notSuitableReasonSpecify'
										label={`${t('member.notSuitableReasonSpecify')}${
											notSuitableReasonIsOther() ? '*' : ''
										}`}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onNext)}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={openAssignProgramWorkers}
				onClose={handleCloseOtherDialog}
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{t('member.closeAllProgramFamilyMemberTitle')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleCloseOtherDialog}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('member.closeAllProgramFamilyMemberText')}
					</Typography>
					<FormProvider {...methods2}>
						<form onSubmit={methods2.handleSubmit(onSubmit)}>
							<Grid
								container
								spacing={2}
								marginTop={0}
								marginBottom={2}
								alignItems='center'
							>
								<Grid size={12} display='flex'>
									<LocalDataGrid
										rows={familyMemberList}
										columns={columns}
										getRowId={(row) => row.id}
										pagination={false}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button
						sx={{ textTransform: 'none' }}
						onClick={() => handleCloseOtherDialog()}
					>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods2.handleSubmit(onSubmit)}
					>
						{t('button.ok')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default CloseNotSuitableDialog;
